import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Button, Divider, Grid, Image } from '@mantine/core';
import { Template, X } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import ResponsiveModal from '../../common/ResponsiveModal';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import StoreCreationStockContentSelection from '../wizard/StoreCreationStockContentSelection';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import CreationWizardButton from '../wizard/CreationWizardButton';
import StoreCreationStoreSelectModal from '../wizard/StoreCreationStoreSelectModal';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';

const ECOM_STORE_THEME_COPY_OPTIONS = [
  // {
  //   label: 'Apply store colors',
  //   description: 'Replace color scheme with your stores branding.',
  //   value: 'applyWebstoreBranding'
  // },
  {
    label: 'Sync all products and collections',
    description:
      'Add any missing products and collections from the theme webstore.',
    value: 'copyProducts'
  }
];

const INITIAL_FORM_STATE = {
  search: '',
  pageIndex: 1,
  ecomStockContent: [],
  ecomStore: null,
  copyOptions: {
    applyStoreBranding: true,
    copyProducts: true
  },
  isStoreModalOpen: false,
  showAllStockContent: false
};

const WebstoreThemeStockContentImport = ({ isOpen, onClose, onAddTheme }) => {
  const { state: authState } = useContext(AuthContext);
  const { state, fetchStoreCreateOptions } = useContext(StoreContext);
  const [formState, setFormState] = useState({ ...INITIAL_FORM_STATE });

  const { loading, value: createOptions } = state.ecomStoreCreateOptions;

  const selectedStockContent = createOptions?.ecomStockContent.find(
    (c) =>
      c.pkEcomStockContent === formState.ecomStockContent[0]?.pkEcomStockContent
  );

  useEffect(() => {
    if (isOpen) {
      if (!createOptions && !loading) {
        fetchStoreCreateOptions(null, triggerNotification);
      }

      setFormState({ ...INITIAL_FORM_STATE });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={
        formState.ecomStore || selectedStockContent
          ? {
              cancelTitle: 'Back',
              onCancel: () => {
                setFormState({
                  ...formState,
                  ecomStore: null,
                  ecomStockContent: []
                });
              },
              onSubmit: () => {
                onAddTheme({
                  pkEcomStore: formState.ecomStore?.pkEcomStore || null,
                  pkEcomStockContent:
                    selectedStockContent?.pkEcomStockContent || null,
                  copyOptions: formState.copyOptions,
                  name: selectedStockContent
                    ? `${selectedStockContent.title} theme`
                    : formState.ecomStore
                    ? `${formState.ecomStore.name} theme`
                    : null
                });
              },
              submitTitle: 'Add theme'
            }
          : {
              onCancel: onClose,
              onSubmit: () => {},
              isSubmitDisabled: true,
              submitTitle: 'Add theme'
            }
      }
      isOpen={isOpen}
      onClose={onClose}
      opened={isOpen}
      size={800}
      title={
        formState.ecomStore
          ? `Copy theme from ${formState.ecomStore.name}`
          : 'Add theme from stock content'
      }
    >
      {formState.ecomStore || selectedStockContent ? (
        <AppStack style={{ gap: 16 }}>
          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            Are you sure you want to copy the theme from{' '}
            {selectedStockContent
              ? selectedStockContent.title
              : formState.ecomStore.name}
            ?
          </AppText>

          <AppStack style={{ gap: 5 }}>
            <AppFlexbox
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8
              }}
            >
              <AppText
                style={{
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Stock content theme
              </AppText>

              <Button
                color="dark"
                leftSection={<X size={18} />}
                onClick={() => {
                  setFormState({
                    ...formState,
                    ecomStore: null,
                    ecomStockContent: []
                  });
                }}
                size="compact-sm"
                variant="outline"
              >
                Unselect
              </Button>
            </AppFlexbox>

            <AppCard radius={8} shadow="sm" style={{ padding: 0 }} withBorder>
              <AppFlexbox
                style={{
                  padding: '10px 16px',
                  gap: 16,
                  alignItems: 'center'
                }}
              >
                <AppStack
                  style={{
                    width: 40,
                    height: 40,
                    border: formState.ecomStore?.logoImageUrl
                      ? 'none'
                      : 'solid 1px #dee2e6',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {formState.ecomStore?.logoImageUrl ? (
                    <Image
                      h={40}
                      src={formState.ecomStore.logoImageUrl}
                      w={40}
                    />
                  ) : (
                    <Template color="#666" size={24} />
                  )}
                </AppStack>
                <AppStack style={{ gap: 0 }}>
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    {selectedStockContent
                      ? selectedStockContent.title
                      : formState.ecomStore.name}
                  </AppText>
                  <AppFlexbox>
                    <Anchor
                      component={Link}
                      style={{
                        color: 'dodgerblue',
                        fontSize: 14,
                        fontWeight: 500
                      }}
                      target="_blank"
                      to={`/store/${
                        selectedStockContent
                          ? selectedStockContent.ecomStore.urlHandle
                          : formState.ecomStore.urlHandle
                      }`}
                    >
                      {selectedStockContent ? 'View theme' : 'View store'}
                    </Anchor>
                  </AppFlexbox>
                </AppStack>
              </AppFlexbox>
            </AppCard>
          </AppStack>

          <AppStack style={{ gap: 10 }}>
            <Divider
              label="Copy options"
              labelPosition="left"
              styles={{label: { color: '#000', fontWeight: 500, fontSize: 14 }}}
            />

            <Grid>
              {ECOM_STORE_THEME_COPY_OPTIONS.map((option) => (
                <Grid.Col key={option.value} span={12}>
                  <CreationWizardButton
                    description={option.description}
                    label={option.label}
                    onClick={() => {
                      setFormState({
                        ...formState,
                        copyOptions: {
                          ...formState.copyOptions,
                          [option.value]: !formState.copyOptions[option.value]
                        }
                      });
                    }}
                    selected={formState.copyOptions[option.value] || false}
                  />
                </Grid.Col>
              ))}
            </Grid>
          </AppStack>
        </AppStack>
      ) : (
        <>
          <StoreCreationStockContentSelection
            createOptions={createOptions}
            formState={formState}
            isSingleSelect
            loading={loading}
            setFormState={setFormState}
            setViewState={setFormState}
            showThemeDetails
            viewState={formState}
          />

          {authState.userData.isAdmin && !formState.showAllStockContent && (
            <AppStack style={{ gap: 10, marginTop: 20 }}>
              <Divider label="Admin options" labelPosition="left" />

              <AppStack style={{ gap: 0 }}>
                <AppStack style={{ gap: 0 }}>
                  <AppText style={{ fontWeight: 500, fontSize: 14 }}>
                    Copy store theme
                  </AppText>
                  <AppText style={{ fontSize: 14, color: '#666' }}>
                    Would you like a theme from another store?
                  </AppText>
                </AppStack>
              </AppStack>

              <CreationWizardButton
                description="Copy theme from an existing store."
                hideCheckbox
                label="Select store to copy"
                onClick={() => {
                  setFormState({
                    ...formState,
                    isStoreModalOpen: true,
                    pageIndex: 1
                  });
                }}
              />
            </AppStack>
          )}

          <StoreCreationStoreSelectModal
            isOpen={formState.isStoreModalOpen}
            onClose={() => {
              setFormState({ ...formState, isStoreModalOpen: false });
            }}
            onSelect={(ecomStore) => {
              setFormState({
                ...formState,
                ecomStore,
                isStoreModalOpen: false,
                pageIndex: 1
              });
            }}
            showStoreLink
          />
        </>
      )}
    </ResponsiveModal>
  );
};

WebstoreThemeStockContentImport.propTypes = {
  isOpen: PropTypes.bool,
  onAddTheme: PropTypes.func,
  onClose: PropTypes.func
};

export default WebstoreThemeStockContentImport;
